import * as React from 'react';
import { useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import { Button, message, Modal, Popconfirm } from 'antd';
import WorkOrderTable from '../WorkOrderTable';
import NoAddWorkOrderHeader from '../WorkOrderTable/TableHeader/noAddWorkOrderHeader';
import WorkOrderTableRowBuyer from '../WorkOrderTable/WorkOrderTableRow/buyer';
import WorkOrderTableRowSupplier from '../WorkOrderTable/WorkOrderTableRow/supplier';
import OWButton from '../ow_button/OWButton';
import { withUserType } from '../withUserType';
import { EmptyState } from '../empty_state/EmptyState';
import { ROLE_TYPES } from '../../utils/DataConstants';
import { nullSafeGet, nullSafeGetOrElse } from '../../utils/DataAccessUtils';
import {
	linkDeliverableWorkOrderForBuyer,
	linkDeliverableWorkOrderForSupplier,
	unlinkDeliverableWorkOrderForBuyer,
	unlinkDeliverableWorkOrderForSupplier,
} from '../../thunks/deliverables_thunks';

const LANDING_TC = 'DELIVERABLE_LINKED_WORK_ORDERS';
const MODAL_TC = 'deliverableLinkWorkOrdersModal';
const MESSAGE_KEY = 'deliverable-wo-op';

const LinkModalHeader = ({ selectedRowKeys, loading, onLink, ...props }: any) => (
	<NoAddWorkOrderHeader
		{...props}
		rightActions={
			selectedRowKeys.length ? (
				<OWButton type="primary" loading={loading} onClick={() => onLink(selectedRowKeys)}>
					Link Selected Work Orders ({selectedRowKeys.length})
				</OWButton>
			) : null
		}
	/>
);

const DeliverableWorkOrders = ({
	deliverable,
	userType,
	currentUser,
	companyConfig,
	location,
	linkWorkOrder,
	unlinkWorkOrder,
}: any) => {
	const deliverableId = nullSafeGet('id', deliverable);
	// The WO search supports an `ids` filter (comma-separated WO ids), so we drive
	// a real WorkOrderTable scoped to the deliverable's linked ids rather than
	// hand-rendering the hydrated docs. workOrderIds is the canonical link list;
	// fall back to the hydrated workOrders' ids.
	const workOrderIds = nullSafeGetOrElse('workOrderIds', deliverable, []);
	const hydratedIds = nullSafeGetOrElse('workOrders', deliverable, []).map((workOrder) =>
		nullSafeGet('id', workOrder)
	);
	const linkedIds = (workOrderIds.length ? workOrderIds : hydratedIds).map(String);
	const idsParam = linkedIds.join(',');
	const WorkOrderTableRow =
		userType === ROLE_TYPES.SUPPLIER ? WorkOrderTableRowSupplier : WorkOrderTableRowBuyer;

	const [modalVisible, setModalVisible] = useState(false);
	const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
	const [loading, setLoading] = useState(false);
	const [unlinkingId, setUnlinkingId] = useState(null);

	const rowSelection = useMemo(
		() => ({
			selectedRowKeys,
			onChange: (keys) => setSelectedRowKeys(keys),
			getCheckboxProps: (record) => ({ disabled: linkedIds.includes(`${record.id}`) }),
		}),
		[selectedRowKeys, linkedIds]
	);

	const landingFilters = useMemo(() => {
		const filters: any = { ids: idsParam };
		if (userType === ROLE_TYPES.SUPPLIER) {
			filters.buyerCompanyId = nullSafeGet('buyerCompanyId', deliverable);
		}
		return filters;
	}, [idsParam, userType, deliverable]);

	const modalFilters = useMemo(() => {
		const filters: any = {};
		if (userType === ROLE_TYPES.SUPPLIER) {
			filters.buyerCompanyId = nullSafeGet('buyerCompanyId', deliverable);
		}
		return filters;
	}, [userType, deliverable]);

	const handleLink = (ids) => {
		setLoading(true);
		message.loading({ content: 'Linking work orders…', key: MESSAGE_KEY });
		// Sequential so each idempotent PATCH sees the previous link applied.
		ids
			.reduce((chain, id) => chain.then(() => linkWorkOrder(deliverableId, id)), Promise.resolve())
			.then(() => {
				message.success({
					content: ids.length > 1 ? 'Work orders linked' : 'Work order linked',
					key: MESSAGE_KEY,
					duration: 1.5,
				});
				setSelectedRowKeys([]);
				setModalVisible(false);
			})
			.catch(() => message.error({ content: 'Failed to link work orders', key: MESSAGE_KEY }))
			.finally(() => setLoading(false));
	};

	const handleUnlink = (workOrderId) => {
		setUnlinkingId(workOrderId);
		message.loading({ content: 'Unlinking work order…', key: MESSAGE_KEY });
		unlinkWorkOrder(deliverableId, workOrderId)
			.then(() =>
				message.success({ content: 'Work order unlinked', key: MESSAGE_KEY, duration: 1.5 })
			)
			.catch(() => message.error({ content: 'Failed to unlink work order', key: MESSAGE_KEY }))
			.finally(() => setUnlinkingId(null));
	};

	// Linked-WO row = the standard WO row plus an Unlink action (mirrors the
	// work_orders_linked tab's CustomWorkOrderRowDisplay).
	const UnlinkRow = ({ record }: any) => {
		const id = nullSafeGet('id', record);
		return (
			<WorkOrderTableRow
				record={record}
				currentUser={currentUser}
				companyConfig={companyConfig}
				location={{ pathname: nullSafeGetOrElse('pathname', location, ''), search: '' }}
				backText="Back to Deliverable"
				actions={[
					<div key="unlink" className="flex flex-col items-center" style={{ width: 200 }}>
						<Popconfirm
							title="Unlink this work order?"
							okText="Unlink"
							okButtonProps={{ danger: true }}
							cancelText="Cancel"
							onConfirm={() => handleUnlink(id)}
						>
							<OWButton type="ghost" loading={unlinkingId === id}>
								Unlink
							</OWButton>
						</Popconfirm>
					</div>,
				]}
			/>
		);
	};

	return (
		<div>
			<div className="mb-3 flex justify-end">
				<Button type="default" size="large" onClick={() => setModalVisible(true)}>
					Link Work Order
				</Button>
			</div>
			{linkedIds.length === 0 ? (
				<EmptyState
					headline="No linked work orders"
					body={<p>Link work orders to track them against this deliverable.</p>}
				/>
			) : (
				// Same presentation as the asset "Active Work Orders" card: a bare
				// WorkOrderTable (no filter header), compact clickable rows.
				<WorkOrderTable
					// Remount when the linked set changes so the table refetches.
					key={idsParam}
					preAppliedFilters={landingFilters}
					targetCollectionName={LANDING_TC}
					CustomWorkOrderRowDisplay={UnlinkRow}
					CustomHeader={() => <></>}
					tableStyle={{
						display: 'flex',
						flexDirection: 'column',
						cursor: 'pointer',
						userSelect: 'none',
					}}
					renderEmptyState={() => <EmptyState headline="No linked work orders" />}
					ignoreRefreshCounts
					openMoreInDefault
					backText="Back to Deliverable"
				/>
			)}

			{modalVisible ? (
				<Modal
					width="80%"
					visible
					title="Link Work Orders"
					closable
					onCancel={() => setModalVisible(false)}
					footer={<Button onClick={() => setModalVisible(false)}>Cancel</Button>}
					wrapClassName="linked-work-orders-modal"
				>
					<WorkOrderTable
						key={MODAL_TC}
						preAppliedFilters={modalFilters}
						renderEmptyState={() => <EmptyState headline="No Work Orders" />}
						rowSelection={rowSelection}
						targetCollectionName={MODAL_TC}
						CustomHeaderProps={{ selectedRowKeys, loading, onLink: handleLink }}
						CustomHeader={LinkModalHeader}
					/>
				</Modal>
			) : null}
		</div>
	);
};

const mapStateToProps = (state) => ({
	userType: state.session.userType,
	currentUser: state.session.currentUser,
	companyConfig: nullSafeGet('company_config.detail', state),
});

const mapDispatchToProps = (dispatch, ownProps) => ({
	linkWorkOrder: (deliverableId, workOrderId) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? linkDeliverableWorkOrderForSupplier(deliverableId, workOrderId)
				: linkDeliverableWorkOrderForBuyer(deliverableId, workOrderId)
		),
	unlinkWorkOrder: (deliverableId, workOrderId) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? unlinkDeliverableWorkOrderForSupplier(deliverableId, workOrderId)
				: unlinkDeliverableWorkOrderForBuyer(deliverableId, workOrderId)
		),
});

export default withUserType(
	withRouter(connect(mapStateToProps, mapDispatchToProps)(DeliverableWorkOrders))
);
